import type { CurrencyFormat, FontSpec, ResolvedTheme, SoundDef, SoundEvent, Theme, ThemeAssets } from '../types';

export type ThemeDefaults = Omit<ResolvedTheme, 'id' | 'name' | 'baseUrl' | 'assets'> & { assets: ThemeAssets };

const SOUNDS: Record<SoundEvent, SoundDef> = {
  click: { synth: 'click', volume: 0.6 },
  spinStart: { synth: 'whoosh', volume: 0.7 },
  spinLoop: { synth: 'hum', volume: 0.25, loop: true },
  reelStop: { synth: 'tick', volume: 0.8 },
  anticipation: { synth: 'riser', volume: 0.6, loop: true },
  scatterLand: { synth: 'chime', volume: 0.8 },
  wildLand: { synth: 'chime', volume: 0.6 },
  winSmall: { synth: 'coin', volume: 0.7 },
  winMedium: { synth: 'fanfare', volume: 0.8 },
  winBig: { synth: 'fanfareBig', volume: 0.9 },
  winMega: { synth: 'fanfareBig', volume: 1 },
  winEpic: { synth: 'fanfareBig', volume: 1 },
  rollup: { synth: 'coin', volume: 0.4, loop: true },
  rollupEnd: { synth: 'sting', volume: 0.8 },
  freeSpinsIntro: { synth: 'fanfareBig', volume: 1 },
  freeSpinsOutro: { synth: 'fanfare', volume: 0.9 },
  retrigger: { synth: 'chime', volume: 0.9 },
  musicBase: { synth: 'ambient', volume: 0.2, loop: true },
  musicFree: { synth: 'ambient', volume: 0.3, loop: true },
  error: { synth: 'buzz', volume: 0.6 },
};

/**
 * Engine defaults: a theme only needs to override what it changes. Symbol images are mandatory.
 * The palette follows the Lucky Fruit "Emerald" design tokens (page #070b12, primary #00ef9b, accent #56f5c5, line #2c3d4c).
 */
export const DEFAULT_THEME: ThemeDefaults = {
  palette: {
    background: '#070b12',
    backgroundFreeSpins: '#080713',
    reelFrame: 'rgba(255, 255, 255, 0.13)',
    reelBackdrop: '#111924',
    reelSeparator: '#2c3d4c',
    symbolDim: 'rgba(7, 11, 18, 0.72)',
    paylines: ['#00ef9b', '#56f5c5', '#9a67ff', '#48e7ff', '#ffd166', '#ff8c42', '#ef476f', '#f4b942', '#ff4d3d', '#9b5de5'],
    text: '#f6f8fb',
    textMuted: '#8fa0af',
    accent: '#00ef9b',
    accentText: '#02120d',
    buttonPrimary: '#00ef9b',
    buttonPrimaryText: '#02120d',
    buttonSecondary: '#1b2734',
    buttonSecondaryText: '#f6f8fb',
    hudBackground: 'rgba(20, 29, 40, 0.92)',
    overlayBackdrop: 'rgba(7, 11, 18, 0.8)',
    winNormal: '#00ef9b',
    winBig: '#56f5c5',
    winMega: '#9a67ff',
    winEpic: '#48e7ff',
    anticipationGlow: '#00ef9b',
    wildGlow: '#9b5de5',
    scatterGlow: '#56f5c5',
    loss: '#ff405c',
  },
  assets: { symbols: {} },
  layout: {
    portrait: {
      designWidth: 390,
      designHeight: 844,
      reels: { x: 3, y: 300, symbolWidth: 72, symbolHeight: 72, gapX: 3, gapY: 3, padding: 6, cornerRadius: 14 },
      logo: { x: 95, y: 120, width: 200 },
      hud: 'bottom',
    },
    landscape: {
      designWidth: 1280,
      designHeight: 720,
      reels: { x: 245, y: 70, symbolWidth: 150, symbolHeight: 150, gapX: 6, gapY: 6, padding: 8, cornerRadius: 18 },
      logo: { x: 30, y: 30, width: 180 },
      hud: 'bottom',
    },
  },
  animations: {
    timing: {
      spin: {
        pressMs: 80,
        windUpMs: 120,
        windUpSymbols: 0.3,
        accelMs: 150,
        speedSymbolsPerSec: 25,
        firstStopMs: 1500,
        staggerMs: 200,
        stopEaseMs: 300,
        overshootSymbols: 0.25,
        stopEasing: 'backOut',
        blur: 0.8,
      },
      quickSpin: { firstStopMs: 600, staggerMs: 100 },
      quickStop: { landWithinMs: 250 },
      anticipation: { extraMsPerReel: 1500 },
      win: {
        revealDelayMs: 250,
        allLinesMs: 1200,
        lineCycleMs: 1000,
        rollupMs: 700,
        rollupBigMs: 2500,
        overlayMinMs: 3000,
        overlaySkippableAfterMs: 1500,
        symbolLoopMs: 800,
        outcomeDelayMs: 1500,
        lineEffectMs: 2000,
      },
      freeSpins: { introMs: 1800, outroMs: 2200, betweenSpinsMs: 600, retriggerToastMs: 1500 },
    },
    effects: {
      symbolWin: 'pulse',
      scatterLand: 'glow',
      wildLand: 'glow',
      anticipationGlow: true,
      line: { width: 6, alpha: 0.9, glow: true, style: 'solid' },
      winOverlay: { coinShower: true, coinCount: 40 },
      winFrame: { enabled: true, appearMs: 250, margin: 0.08 },
      outcomeCard: { enabled: false },
    },
  },
  sounds: SOUNDS,
  fonts: {
    display: { family: "'Manrope', 'Arial Black', Arial, Helvetica, sans-serif", weight: 800 },
    ui: { family: "'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif", weight: 700 },
  },
  strings: {},
};

export const DEFAULT_CURRENCY: CurrencyFormat = { code: 'FUN', symbol: '$', decimals: 2, position: 'prefix' };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep merge: objects recurse, arrays and primitives replace. `undefined` in the override is ignored. */
export function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlainObject(override)) return (override === undefined ? base : override) as T;
  const out: Record<string, unknown> = isPlainObject(base) ? { ...(base as Record<string, unknown>) } : {};
  for (const [k, v] of Object.entries(override)) {
    if (v === undefined) continue;
    const b = out[k];
    out[k] = isPlainObject(v) && isPlainObject(b) ? deepMerge(b, v) : isPlainObject(v) ? deepMerge({}, v) : v;
  }
  return out as T;
}

const ABSOLUTE = /^(?:[a-z][a-z0-9+.-]*:|\/)/i;

/** Resolve a theme-relative asset path against the theme folder URL. */
export function resolveAsset(path: string, baseUrl: string): string {
  if (!path || ABSOLUTE.test(path)) return path;
  return baseUrl + path.replace(/^\.\//, '');
}

/** Merge a theme.json (partial) over the defaults and resolve every asset path. */
export function mergeTheme(base: ThemeDefaults, theme: Theme, baseUrl: string): ResolvedTheme {
  if (!baseUrl.endsWith('/')) baseUrl += '/';
  const merged = deepMerge(base as unknown as ResolvedTheme, theme) as ResolvedTheme;
  merged.id = theme.id;
  merged.name = theme.name ?? theme.id;
  merged.baseUrl = baseUrl;
  const r = (p?: string) => (p ? resolveAsset(p, baseUrl) : p);

  const symbols: ThemeAssets['symbols'] = {};
  for (const [id, a] of Object.entries(merged.assets.symbols ?? {})) {
    if (!a) continue;
    symbols[id] = {
      image: resolveAsset(a.image, baseUrl),
      blurImage: r(a.blurImage),
      frames: a.frames?.map((f) => resolveAsset(f, baseUrl)),
      fps: a.fps,
      winSheet: a.winSheet ? { ...a.winSheet, image: resolveAsset(a.winSheet.image, baseUrl) } : undefined,
    };
  }
  merged.assets = {
    ...merged.assets,
    symbols,
    background: merged.assets.background
      ? { base: r(merged.assets.background.base), freeSpins: r(merged.assets.background.freeSpins) }
      : undefined,
    reelFrame: r(merged.assets.reelFrame),
    logo: r(merged.assets.logo),
    coin: r(merged.assets.coin),
    brandSignature: r(merged.assets.brandSignature),
    outcomeCard: merged.assets.outcomeCard?.win ? { win: resolveAsset(merged.assets.outcomeCard.win, baseUrl) } : undefined,
    winFrame: merged.assets.winFrame?.image ? { ...merged.assets.winFrame, image: resolveAsset(merged.assets.winFrame.image, baseUrl) } : undefined,
    spinButton: merged.assets.spinButton?.default
      ? { default: resolveAsset(merged.assets.spinButton.default, baseUrl), pressed: resolveAsset(merged.assets.spinButton.pressed, baseUrl), disabled: resolveAsset(merged.assets.spinButton.disabled, baseUrl) }
      : undefined,
    mascot: merged.assets.mascot?.idle
      ? {
          idle: resolveAsset(merged.assets.mascot.idle, baseUrl),
          win: r(merged.assets.mascot.win),
          loss: r(merged.assets.mascot.loss),
          poster: r(merged.assets.mascot.poster),
          hevc: merged.assets.mascot.hevc
            ? { idle: r(merged.assets.mascot.hevc.idle), win: r(merged.assets.mascot.hevc.win), loss: r(merged.assets.mascot.hevc.loss) }
            : undefined,
        }
      : undefined,
  };
  for (const def of Object.values(merged.sounds)) {
    if (!def?.src) continue;
    def.src = Array.isArray(def.src) ? def.src.map((s) => resolveAsset(s, baseUrl)) : resolveAsset(def.src, baseUrl);
  }
  merged.fonts = {
    display: resolveFont(merged.fonts.display, baseUrl)!,
    ui: resolveFont(merged.fonts.ui, baseUrl)!,
    mono: resolveFont(merged.fonts.mono, baseUrl),
  };
  merged.strings = { ...(merged.strings ?? {}) };
  return merged;
}

/** Resolve a font's file/stylesheet paths against a folder URL. */
export function resolveFont(f: FontSpec | undefined, baseUrl: string): FontSpec | undefined {
  if (!f) return undefined;
  return { ...f, src: f.src ? resolveAsset(f.src, baseUrl) : undefined, css: f.css ? resolveAsset(f.css, baseUrl) : undefined };
}
